import { ArrowCounterClockwise } from '@phosphor-icons/react';
import { cloneElement, ReactElement, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from 'react-query';

import { ComponentStatus } from 'bom/enums';
import { useBomComponentActions } from 'bom/hooks';
import { Button } from 'core/components';
import { useAgGridTableContext, useSelectionCountContext } from 'core/states';
import { IJobPart } from 'jobs/types';
import { Color, Variant } from 'theme/enums';

interface IProps {
  jobPart?: IJobPart;
  buttonElement?: ReactElement;
}

export const RestoreButton = ({ jobPart, buttonElement }: IProps) => {
  const queryClient = useQueryClient();

  const { t } = useTranslation();

  const { selectedRows, deselectAllRows } = useAgGridTableContext();
  const { selectionCount } = useSelectionCountContext();

  const { restoreBomComponents } = useBomComponentActions({ selectionCount });

  const rowsToRestore = useMemo(
    () => (selectedRows.length > 0 ? selectedRows : [jobPart]),
    [jobPart, selectedRows]
  );

  const isDisabled = useMemo(
    () =>
      rowsToRestore.length === 0 ||
      !rowsToRestore.every((row) => row.status === ComponentStatus.DELETED),
    [rowsToRestore]
  );

  const onRestore = useCallback(async () => {
    const ids = rowsToRestore.map((row) => row.componentId).filter(Boolean);

    if (ids.length === 0) {
      return;
    }

    const { success } = await restoreBomComponents(ids);

    if (!success) {
      return;
    }

    deselectAllRows();
    queryClient.invalidateQueries(['jobParts']);
  }, [rowsToRestore, restoreBomComponents, deselectAllRows, queryClient]);

  return useMemo(() => {
    if (buttonElement) {
      return cloneElement(buttonElement, { onClick: onRestore, disabled: isDisabled });
    }

    return (
      <Button
        text={t('buttonLabelRestore')}
        variant={isDisabled ? Variant.OUTLINED : Variant.SOFT}
        color={Color.SUCCESS}
        startDecorator={<ArrowCounterClockwise weight="bold" />}
        onClick={onRestore}
        disabled={isDisabled}
      />
    );
  }, [buttonElement, t, isDisabled, onRestore]);
};
